import { GrowthBook } from '@growthbook/growthbook-react'
import axios, { AxiosError, AxiosResponse } from 'axios'
import axiosRetry from 'axios-retry'
import { useEffect, useState } from 'react'
import { emitExperimentViewedGAEvent } from '../ga-event-helpers'

axiosRetry(axios, {
	retries: 3,
	retryDelay: (retryCount) => {
		// console.log(`retry attempt: ${retryCount}`)
		return retryCount * 2000
	},
})

// Module-level dedup for the `experiment_viewed` GA event. A fresh GrowthBook
// instance is created per hook mount and Header (which mounts this hook) is
// rendered per-page, so GrowthBook's per-instance trackingCallback dedup is
// wiped on every SPA navigation. Without this module-level guard the same
// experiment_viewed re-fires on each navigation. Keyed by `${key}:${variation}`.
const emittedExperimentViews = new Set<string>()

export const useGrowthbook = (isMainPage: boolean) => {
	const experimentEndpoint = `${process.env.NEXT_PUBLIC_BACKEND_URL}/hotels/experiment?api-key=${process.env.NEXT_PUBLIC_API_KEY}`
	const [growthbook, setGrowtBookInstance] = useState<
		GrowthBook<Record<string, any>>
	>(
		() => new GrowthBook({
			enableDevMode: process.env.NODE_ENV !== 'production',
			apiHost: 'https://cdn.growthbook.io/',
			clientKey: process.env.NEXT_PUBLIC_GROWTHBOOK_API_KEY,
			trackingCallback: (experiment, result) => {
				// console.log('Tracking callback called')

				const dedupeKey = `${experiment.key}:${result.variationId}`

				const interval = setInterval(() => {
					if (window.dataLayer) {
						clearInterval(interval)

						// Prevent sending 'bhb-sign-up' experiment event from non-main pages
						if (experiment.key === 'bhb-sign-up' && !isMainPage) {
							return
						}

						// Emit at most once per (experiment, variation) across the whole
						// session — GrowthBook's own dedup resets on every remount/nav.
						if (emittedExperimentViews.has(dedupeKey)) {
							return
						}
						emittedExperimentViews.add(dedupeKey)

						// console.log('GA, tracking callback called')
						emitExperimentViewedGAEvent(
							experiment.key,
							result.variationId.toString(),
							new Date().toISOString(),
						)
					}
				}, 2000)
				
				axios
					.post(
						experimentEndpoint,
						{
							experiment_id: experiment.key,
							variation_id: result.variationId.toString(),
							event_timestamp: new Date().toISOString(),
						},
						{
							withCredentials: true,
						}
					)
					.then((response: AxiosResponse) => {
						// console.log(JSON.stringify('experiment viewed', response.data))
					})
					.catch((error: AxiosError) => {
						console.log(error)
					})
			},
		})
	)

	useEffect(() => {
		if (growthbook) {
			growthbook.setAttributes({
				url: window.location.href,
			})
			growthbook.init()
		}
	}, [growthbook])

	const gbCallback = (params: { uid: string }) => {
		const { uid } = params

		growthbook.setAttributes({
			id: uid,
			url: window.location.href,
		})
	}
	return { growthbook, gbCallback }
}
